import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Editor } from "@tinymce/tinymce-react";
import { PropTypes } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import MentionsEditor from "~/components/forms/HtmlEditors/MentionsEditor";
import GlobalContainer from "~/components/global_container";
import AssistModal from "~/components/modals/AiAssist/Modal";
import { ACTIVITY_TYPE_TO_EDITOR_TYPE } from "~/constants/EditorTypes";
import {
  ACTIVITY_EDITOR_PLUGINS,
  ACTIVITY_EDITOR_TOOLBAR,
  ACTIVITY_VARIABLE_EDITOR_TOOLBAR,
  IMAGES_UPLOAD_HANDLER,
  TINYMCE_SCRIPT_PATH,
  TINYMCE_SCROLLABLE_UI_OPTIONS,
} from "~/constants/TinyMCE";
import EditorHelpers from "~/helpers/editor_helpers";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import { initWebSpellChecker } from "~/lib/web_spell_checker";

const ActivityEditor = ({
  id = "",
  className = "",
  placeholder = "Enter text or insert / drop images ...",
  value = "",
  onChange = () => false,
  activityType = "note",
  leadId = null,
  spellCheck = false,
  linksToInsert = [],
  clearLinksToInsert,
  replaceValue = null,
  clearReplaceValue = () => false,
  enableVariables = false,
}) => {
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const [hasCheckedFeature, setHasCheckedFeature] = useState(false);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(value || "");
  const [showAssistModal, setShowAssistModal] = useState(false);
  const editorRef = useRef(null);
  const init = {
    placeholder,
    ...TINYMCE_SCROLLABLE_UI_OPTIONS,
    cache_suffix: "?v=7.9.1",
    license_key: "gpl",
    plugins: ACTIVITY_EDITOR_PLUGINS,
    toolbar: enableVariables
      ? ACTIVITY_VARIABLE_EDITOR_TOOLBAR
      : ACTIVITY_EDITOR_TOOLBAR,
    toolbar_mode: "wrap",
    branding: false,
    menubar: false,
    file_picker_types: "image",
    min_height: 350,
    relative_urls: false,
    remove_script_host: false,
    document_base_url: Rails.baseUrl,
    forced_root_block: "div",
    entity_encoding: "named",
    content_style: "body { font-family: Arial; font-size: 13px; }",
    paste_as_text: false,
    contextmenu: false,
    paste_remove_styles_if_webkit: false,
    statusbar: true,
    images_upload_handler: IMAGES_UPLOAD_HANDLER,
    setup: (editor) => {
      if (Rails.abilities.manageLead) {
        editor.ui.registry.addButton("ai-assist", {
          icon: "ai",
          tooltip: "AI Assist",
          onAction: () => setShowAssistModal(true),
        });
      }

      if (enableVariables) {
        EditorHelpers.addVariablesMenu(editor, {
          includeSender: false,
          includeOwner: true,
          includeAlertVariables: true,
        });
      }

      EditorHelpers.addVisitLink(editor);
    },
  };

  useEffect(() => {
    const handlePosthogReady = () => {
      const isAccessAllowed = checkFeatureFlag(
        process.env.SHAKAPACKER_PUBLIC_ALLOW_MENTIONS_FLAG,
      );
      setIsFeatureEnabled(isAccessAllowed);
      setHasCheckedFeature(true);
    };

    if (window.isPosthogInitialized) {
      handlePosthogReady();
    } else {
      window.addEventListener("posthogReady", handlePosthogReady);
    }

    return () => {
      window.removeEventListener("posthogReady", handlePosthogReady);
    };
  }, []);

  useEffect(() => {
    if (replaceValue != null) {
      setContent(replaceValue);
      clearReplaceValue();
    }
  }, [replaceValue, clearReplaceValue]);

  useEffect(() => {
    if (linksToInsert?.length) {
      let newContent = content;
      linksToInsert.forEach((link) => {
        const { text, href } = link;
        const linkHtml = `<br/><a href="${href}" target="_blank">${text}</a>&nbsp;`;

        newContent += linkHtml;
      });
      setContent(newContent);
      clearLinksToInsert();
    }
  }, [linksToInsert, clearLinksToInsert]);

  const handleEditorChange = (_value, editor) => {
    const newContent = editor.getContent({ format: "html" });
    setContent(newContent);
    onChange(newContent);
  };

  const handleOnInit = (_event, editor) => {
    editorRef.current = editor;
    setLoading(false);

    if (value) {
      editor.setContent(value, { format: "html" });
    }

    if (spellCheck) {
      initWebSpellChecker(editor.iframeElement);
    }
  };
  return (
    <div className={`${className} position-relative`}>
      {loading && (
        <div className="d-flex justify-content-center align-items-center position-absolute w-100 h-100 bg-white z-5">
          <FontAwesomeIcon icon="far fa-spinner" pulse className="mr5" />{" "}
          Loading
        </div>
      )}

      {hasCheckedFeature && isFeatureEnabled ? (
        <MentionsEditor
          id={id}
          value={content}
          onEditorChange={handleEditorChange}
          onInit={handleOnInit}
          init={init}
          activityType={activityType}
        />
      ) : (
        <Editor
          id={id}
          tinymceScriptSrc={TINYMCE_SCRIPT_PATH}
          scriptLoading={{ async: true }}
          value={content}
          onEditorChange={handleEditorChange}
          onInit={handleOnInit}
          init={init}
        />
      )}

      {showAssistModal && (
        <AssistModal
          show
          onHide={() => setShowAssistModal(false)}
          editor={editorRef.current}
          editorType={
            ACTIVITY_TYPE_TO_EDITOR_TYPE[activityType] || activityType
          }
          leadId={leadId}
          product={GlobalContainer.product()}
        />
      )}
    </div>
  );
};

ActivityEditor.propTypes = {
  value: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  activityType: PropTypes.string,
  leadId: PropTypes.number,
  spellCheck: PropTypes.bool,
  linksToInsert: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ),
  clearLinksToInsert: PropTypes.func,
  replaceValue: PropTypes.string,
  clearReplaceValue: PropTypes.func,
  enableVariables: PropTypes.bool,
};

export default ActivityEditor;
